// API & AI: the keys a brand gives Claude, ChatGPT, its own agent or a
// script, and how to connect one. A key acts as the brand, with the portal's
// own rules: publishing is immediate, just as it is in the portal. The secret
// is shown once, right after it is made.

import {useCallback, useEffect, useState} from 'react';
import type {FormEvent} from 'react';
import {Link} from 'react-router-dom';
import type {ApiKey, ApiKeyAccess, ApiKeyActivity, ApiKeyListResponse} from '../../shared/api-keys';
import {Icon} from '../creator/components/Icon';
import {createApiKey, describe, fetchApiKeys, revokeApiKey} from './api';
import {usePageTitle} from './page-title';
import {useBrand} from './store';
import './agents.css';

const CAN = [
  'Draft and publish campaigns',
  'Pick creators from the applicants',
  'Approve videos or ask for changes',
  'Message creators',
  'Read results: views and tiers per post',
];

/** What an agent did, in words; a tool not named here reads as its own name. */
const DID: Record<string, string> = {
  api_create_campaign: 'Created a campaign', api_update_campaign: 'Edited a campaign', api_submit_campaign: 'Published a campaign',
  api_pick_applicant: 'Picked a creator', api_review_video: 'Decided on a video', api_send_message: 'Messaged a creator',
  api_mark_thread_read: 'Read messages', api_create_upload_link: 'Made an upload link',
  api_key_created: 'Key created', api_key_revoked: 'Key revoked',
};

function ActivityRow({item}: {item: ApiKeyActivity}) {
  const did = DID[item.action] ?? item.action.replace(/^api_/, '').replace(/_/g, ' ');
  return (
    <li>
      <span><b>{item.key_name}</b> · {did}{item.campaign && item.campaign_id ? <> · <Link to={`/campaigns/${item.campaign_id}`}>{item.campaign}</Link></> : null}</span>
      <small>{new Date(item.at).toLocaleString(undefined, {day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'})}</small>
    </li>
  );
}

function when(value: string | null): string {
  return value ? new Date(value).toLocaleDateString(undefined, {day: 'numeric', month: 'short', year: 'numeric'}) : 'Never';
}

function CopyLine({label, text}: {label: string; text: string}) {
  const {toast} = useBrand();
  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      toast(`${label} copied`);
    } catch {
      toast('Could not copy. Select the text and copy it.', true);
    }
  }
  return (
    <div className="copy-line">
      <pre>{text}</pre>
      <button className="btn btn-light btn-sm" type="button" onClick={() => void copy()}><Icon name="link" size={15} />Copy</button>
    </div>
  );
}

function KeyRow({apiKey, onRevoked}: {apiKey: ApiKey; onRevoked(): void}) {
  const {toast} = useBrand();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!confirming) return;
    const timer = window.setTimeout(() => setConfirming(false), 4000);
    return () => window.clearTimeout(timer);
  }, [confirming]);
  async function revoke() {
    setBusy(true);
    try {
      await revokeApiKey(apiKey.id);
      toast(`${apiKey.name} can no longer act for you`);
      onRevoked();
    } catch (error) {
      toast(describe(error), true);
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  }
  const revoked = Boolean(apiKey.revoked_at);
  return (
    <article className={`key-row${revoked ? ' revoked' : ''}`}>
      <span className="key-icon"><Icon name={revoked ? 'lock' : 'spark'} size={18} /></span>
      <div className="key-who">
        <b>{apiKey.name}</b>
        <small><code>{apiKey.prefix}…</code> · {revoked ? `Revoked ${when(apiKey.revoked_at)}` : `Last used ${apiKey.last_used_at ? when(apiKey.last_used_at) : 'never'}`}</small>
      </div>
      <span className={`status-pill ${revoked ? '' : apiKey.access === 'write' ? 'active' : 'in_review'}`}>{revoked ? 'Revoked' : apiKey.access === 'write' ? 'Full access' : 'Read only'}</span>
      {revoked ? <span className="key-spacer" /> : (
        <button className={`btn btn-sm ${confirming ? 'btn-danger' : 'btn-light'}`} type="button" disabled={busy}
          onClick={() => (confirming ? void revoke() : setConfirming(true))}>
          {busy ? 'Revoking…' : confirming ? 'Confirm revoke' : 'Revoke'}
        </button>
      )}
    </article>
  );
}

export function AgentsPage() {
  const {data, toast} = useBrand();
  usePageTitle('API & AI');
  const [keys, setKeys] = useState<ApiKeyListResponse | null>(null);
  const [name, setName] = useState('');
  const [access, setAccess] = useState<ApiKeyAccess>('write');
  const [secret, setSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const load = useCallback(() => fetchApiKeys().then(setKeys).catch((caught) => toast(describe(caught), true)), [toast]);
  useEffect(() => { void load(); }, [load]);
  if (!data) return null;
  const signed = data.agreement.status === 'signed';

  async function create(event: FormEvent) {
    event.preventDefault();
    setBusy(true); setError('');
    try {
      const created = await createApiKey(name.trim(), access);
      setSecret(created.secret);
      setName('');
      await load();
    } catch (caught) {
      setError(describe(caught));
    } finally {
      setBusy(false);
    }
  }

  const mcp = keys?.endpoints.mcp ?? `${window.location.origin}/mcp`;
  const api = keys?.endpoints.api ?? `${window.location.origin}/api/v1`;
  const shown = secret || '<your key>';
  const live = (keys?.keys ?? []).filter((key) => !key.revoked_at);
  const revoked = (keys?.keys ?? []).filter((key) => key.revoked_at);

  return (
    <div className="page">
      <header className="page-head">
        <div>
          <p className="eyebrow">API & AI</p>
          <h1>Let your AI run campaigns.</h1>
          <p className="lead">Claude, ChatGPT or your own agent works here with a key, under the same rules as you. Published campaigns go live immediately.</p>
        </div>
      </header>

      {keys && !keys.enabled ? <p className="form-alert" role="alert">The Earn team has paused the API for now. Your keys work again once it is back.</p> : null}

      <div className="agents-layout">
        <section className="card brand-section">
          <div className="section-head"><div><h2>Keys</h2><p>One key per agent or tool, so you can revoke one alone.</p></div></div>
          {signed ? (
            <form className="key-form" onSubmit={(event) => void create(event)}>
              <label className="brand-field"><span>Name</span><input value={name} onChange={(event) => setName(event.target.value)} placeholder="Claude, Zapier, our agent…" maxLength={80} required /></label>
              <label className="brand-field"><span>Access</span>
                <select value={access} onChange={(event) => setAccess(event.target.value as ApiKeyAccess)}>
                  <option value="write">Full access</option>
                  <option value="read">Read only</option>
                </select>
              </label>
              <button className="btn btn-g" disabled={busy || !name.trim()}>{busy ? 'Creating…' : 'Create key'}<Icon name="plus" size={16} /></button>
            </form>
          ) : (
            <div className="key-locked"><Icon name="lock" size={18} /><span>Sign the agreement first: an agent acts for your company.</span><Link to="/agreement">Read agreement</Link></div>
          )}
          {error ? <p className="form-alert">{error}</p> : null}
          {secret ? (
            <div className="key-secret" role="status">
              <b>Copy it now: we only show it once.</b>
              <CopyLine label="Key" text={secret} />
              <button className="text-button" type="button" onClick={() => setSecret('')}>I saved it</button>
            </div>
          ) : null}
          {keys === null ? <div className="empty-state"><span className="loader" /></div> : live.length + revoked.length === 0 ? (
            <div className="empty-state"><Icon name="spark" size={28} /><b>No keys yet</b><span>Create one and paste it into your agent.</span></div>
          ) : (
            <div className="key-list">{[...live, ...revoked].map((key) => <KeyRow key={key.id} apiKey={key} onRevoked={() => { setSecret(''); void load(); }} />)}</div>
          )}
        </section>

        <aside className="card brand-section">
          <h2>What an agent can do</h2>
          <ul className="agent-can">{CAN.map((item) => <li key={item}><Icon name="check" size={16} />{item}</li>)}</ul>
          <p className="muted">Signing the agreement and making keys stay with you.</p>
        </aside>
      </div>

      {keys?.activity?.length ? (
        <section className="card brand-section">
          <div className="section-head"><div><h2>What your agents did</h2><p>Every change made with one of your keys, newest first.</p></div></div>
          <ul className="agent-activity">{keys.activity.map((item) => <ActivityRow key={`${item.at}-${item.action}-${item.key_name}`} item={item} />)}</ul>
        </section>
      ) : null}

      <section className="card brand-section connect">
        <div className="section-head"><div><h2>Connect</h2><p>The same tools over MCP and plain HTTP.</p></div></div>
        <div className="connect-grid">
          <div><h3>Claude Code</h3><CopyLine label="Command" text={`claude mcp add --transport http earn ${mcp} --header "Authorization: Bearer ${shown}"`} /></div>
          <div><h3>Cursor, Claude Desktop and other MCP clients</h3><CopyLine label="Config" text={JSON.stringify({mcpServers: {earn: {url: mcp, headers: {Authorization: `Bearer ${shown}`}}}}, null, 2)} /></div>
          <div><h3>HTTP</h3><CopyLine label="Command" text={`curl ${api} -H "Authorization: Bearer ${shown}"`} /><small className="muted">Lists every tool with its JSON Schema. Each one is POST {api}/&lt;tool&gt;.</small></div>
        </div>
      </section>
    </div>
  );
}
